import React, { useState, useEffect, useRef } from 'react';
import { VectorMap } from "@react-jvectormap/core";
import { worldMill } from "@react-jvectormap/world";
import { CountriesUIFlag } from 'ui-component/CountriesUI';
import { Skeleton, Tooltip } from '@mui/material';
import { FaSearch } from 'react-icons/fa';
import DemographicChartModal from './modal/DemographicChartModal'
import { SortDescNumberIcon, SortAscNumberIcon, SortDescAlphabeticalIcon, SortAscAlphabeticalIcon } from 'ui-component/custom-icons/icons';
import { PiMicrosoftExcelLogoFill } from 'react-icons/pi';
import ExportPopupModal from './modal/ExportPopupModal';


// ✅ Clear
const DemographicChartComponent = ({ mapCount, mapList, isLoading }) => {

    const [regionValues, setRegionValues] = useState({});
    const [datas, setDatas] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const regionValuesRef = useRef(regionValues);
    const mapColor = '#0066fb';
    const searchTimeout = useRef(null);
    const [sortBy, setSortBy] = useState('percentage')
    const [sortOrder, setSortOrder] = useState('desc')
    const [isLoadingData, setIsLoadingData] = useState(true)
    const [showExportModal, setShowExportModal] = useState(false);
    const [isComponentLoading, setIsComponentLoading] = useState(false);
    const customMap = {
        ...worldMill, // Spread existing world map data
        content: {
            ...worldMill.content, // Retain original content structure
            paths: {
                ...worldMill.content.paths, // Retain original country paths
                FR: {
                    name: "France",
                    path: "M444.48,172.62l-0.64,1.78l-0.58,-0.31l-0.49,-1.72l0.4,-0.89l1.0,-0.72l0.3,1.85ZM429.64,147.1l1.78,1.58l1.46,-0.13l2.1,1.42l1.35,0.27l1.23,0.83l3.04,0.5l-1.03,1.85l-0.3,2.12l-0.41,0.32l-0.95,-0.24l-0.5,0.43l0.06,0.61l-1.81,1.92l-0.04,1.42l0.55,0.38l0.88,-0.36l0.61,0.97l-0.03,1.0l0.57,0.91l-0.75,1.09l0.65,2.39l1.27,0.57l-0.18,0.82l-2.01,1.53l-4.77,-0.8l-3.82,1.0l-0.53,1.85l-2.49,0.34l-2.71,-1.31l-1.16,0.57l-4.31,-1.29l-0.72,-0.86l1.19,-1.78l0.39,-6.45l-2.58,-3.3l-1.9,-1.66l-3.72,-1.23l-0.19,-1.72l2.81,-0.61l4.12,0.81l0.47,-0.48l-0.6,-2.77l1.94,0.95l5.83,-2.54l0.92,-2.74l1.6,-0.49l0.24,0.78l1.36,0.33l1.05,1.19Z",
                },
                GF: {
                    name: "French Guiana",
                    path: "M289.01,278.39l-0.81,0.8l-0.78,0.12l-0.5,-0.66l-0.56,-0.1l-0.91,0.6l-0.46,-0.22l1.09,-2.96l-0.96,-1.77l-0.17,-1.49l1.07,-1.77l2.32,0.75l2.51,2.01l0.3,0.74l-2.14,3.96Z",
                },
            },
        },
    };


    // Debounced search effect
    useEffect(() => {
        setIsComponentLoading(true)
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            if (searchValue) {
                let filtered = mapList;
                        if (searchValue) {
                            filtered = mapList.filter(item =>
                                (item.name || '').toLowerCase().includes(searchValue.toLowerCase())
                            ) || [];
                        }

                let sorted = [...filtered];

                if (sortBy === 'name') {
                    sorted.sort((a, b) => {
                        const nameA = a.name || '';
                        const nameB = b.name || '';
                        if (sortOrder === 'asc') {
                            return nameA.localeCompare(nameB);
                        } else {
                            return nameB.localeCompare(nameA);
                        }
                    });
                } else if (sortBy === 'percentage') {
                    sorted.sort((a, b) => {
                        if (sortOrder === 'asc') {
                            return parseInt(a.percentage) - parseInt(b.percentage);
                        } else {
                            return parseInt(b.percentage) - parseInt(a.percentage);
                        }
                    });
                }
                setDatas(sorted);
            } else {

                let filtered = Array.isArray(mapList) ? mapList : [];

                if (sortBy === 'name') {
                    filtered.sort((a, b) => {
                        const nameA = a.name || '';
                        const nameB = b.name || '';
                        if (sortOrder === 'asc') {
                            return nameA.localeCompare(nameB);
                        } else {
                            return nameB.localeCompare(nameA);
                        }
                    });
                } else if (sortBy === 'percentage') {
                    filtered.sort((a, b) => {
                        if (sortOrder === 'asc') {
                            return parseInt(a.percentage) - parseInt(b.percentage);
                        } else {
                            return parseInt(b.percentage) - parseInt(a.percentage);
                        }
                    });
                }
                setDatas(filtered);
            }
        setIsComponentLoading(false)
        }, 400); // 400ms debounce
        return () => clearTimeout(searchTimeout.current);
    }, [searchValue, mapList, sortBy, sortOrder]);

    useEffect(() => {
        setRegionValues(mapCount || {});
    }, [mapCount]);

    useEffect(() => {
        setDatas(mapList || []);
    }, [mapList]);

    useEffect(() => {
        regionValuesRef.current = regionValues;
    }, [regionValues]);
    
    const handleClose = () => {
        setShowExportModal(false);
    }


    return (
        <>
            <div className="card" >
                <div className="card-body" style={{ padding: '15px', zIndex: '1000', zIndex: '1' }}>
                    <div className="flex justify-content-between">
                        <div className="text-lg fw-500">
                            Contact Locations
                        </div>
                        <div className="flex gap-1 absolute" style={{ right: '20px', top: '6px' }}>
                            {
                                isLoading ? (
                                    <div className="" style={{ width: '100px', marginTop: '8px' }}>
                                        <Skeleton variant="rounded" width={'100%'} height={20} className={`rounded-full`} style={{ borderRadius: '32px' }} />
                                    </div>
                                ) : (
                                    <>
                                        <div
                                            className="widget-action-icon-container"
                                            onClick={() => {
                                                setShowExportModal(true)
                                            }}
                                        >
                                            <Tooltip title="Export" arrow placement="top">

                                                <div
                                                    href="javascript:void(0);" 
                                                    className="text-black cursor-pointer"
                                                >

                                                    <PiMicrosoftExcelLogoFill style={{ fontSize: '1.4em' }} /> 
                                                </div>
                                            </Tooltip>
                                        </div>
                                        <div
                                            className={`widget-action-icon-container-2 ${sortBy === 'name' ? 'active' : '' }`}
                                            onClick={() => {
                                                setSortBy('name')
                                                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                                            }}
                                        >
                                            <Tooltip title="Sort by Name" arrow placement="top">

                                                <div
                                                    href="javascript:void(0);" 
                                                    className="widget-action-icon cursor-pointer"
                                                >
                                                    {
                                                        sortBy === 'name' ? (
                                                            sortOrder === 'asc' ? (
                                                                <SortAscAlphabeticalIcon height='15px' width='15px' />
                                                            ) : (
                                                                <SortDescAlphabeticalIcon height='15px' width='15px' />
                                                            )
                                                        ) : (
                                                            <SortDescAlphabeticalIcon height='15px' width='15px' />
                                                        )
                                                    }
                                                </div>
                                            </Tooltip>
                                        </div>
                                        <div
                                            className={`widget-action-icon-container-2 ${sortBy === 'percentage' ? 'active' : '' }`}
                                            onClick={() => {
                                                setSortBy('percentage')
                                                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                                            }}
                                        >
                                            <Tooltip title="Sort by Percentage" arrow placement="top">
                                                <div
                                                    href="javascript:void(0);" 
                                                    className="widget-action-icon cursor-pointer"
                                                >

                                                    {
                                                        sortBy === 'name' ? (
                                                            <SortDescNumberIcon height='15px' width='15px' />
                                                        ) : (
                                                            sortOrder === 'asc' ? (
                                                                <SortAscNumberIcon height='15px' width='15px' />
                                                            ) : (
                                                                <SortDescNumberIcon height='15px' width='15px' />
                                                            )
                                                        )
                                                    }
                                                </div>
                                            </Tooltip>
                                        </div>
                                    </>
                                )
                            }
                        </div>
                    </div>
                    {
                        isLoading ? (
                            <>
                                <div className="row" style={{ marginTop: '10px' }}>
                                    <div className="col-12">
                                        <Skeleton variant="rounded" width={'100%'} height={200} style={{ borderRadius: '12px' }} />
                                    </div>
                                </div>
                                <div className="row" style={{ marginTop: '10px', marginBottom: '10px' }}>
                                    <div className="col-12">
                                        <div className="flex justify-content-end" style={{ marginBottom: '5px'}}>
                                            <div style={{ width: '150px' }}>
                                                <Skeleton variant="rounded" width={'100%'} height={25} style={{ borderRadius: '32px' }} />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div
                                        className="col-12"
                                        style={{
                                            height: '267px',
                                            overflowY: 'auto',
                                        }}
                                    >

                                        {[...Array(25)].map((e, i) => (
                                            <div className="row" key={i} style={{ marginBottom: '10px' }}>
                                                <div className="col-12">
                                                    <div className="w-full flex justify-between items-center" style={{ padding: '2px 0' }}>
                                                        <div className="flex gap-2 align-items-center">
                                                            <div style={{ fontSize: '35px', width: '40px' }}>
                                                                <Skeleton variant="rounded" width={'100%'} height={35} style={{ borderRadius: '12px' }} />
                                                            </div>
                                                            <div>
                                                                <div className="font-14" style={{ width: '150px', marginBottom: '5px' }}>
                                                                    <Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
                                                                </div>
                                                                <div className="text-gray-600" style={{ width: '100px' }}>
                                                                    <Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-2 align-items-center">
                                                            <div>
                                                                <div className="text-gray-600" style={{ width: '100px' }}>
                                                                    <Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
                                                                </div>
                                                            </div>
                                                            <div className="text-gray-600" style={{ width: '25px' }}>
                                                                <Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div
                                    className="col-12 flex justify-content-center"
                                    style={{
                                        marginTop: '10px',
                                        marginBottom: '-10px',
                                        textAlign: 'center',
                                        fontSize: '14px',
                                    }}
                                >
                                    <Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="row">
                                    <div className="col-12">
                                        <div style={{ width: "100%", height: '200px', padding: "10px", borderRadius: "12px", background: "#fbfbfb", boxShadow: "0px 0px 3px #bfbfbf", margin: "10px 0 15px 0" }}>
                                                <VectorMap
                                                    map={customMap}
                                                    backgroundColor="transparent"
                                                    zoomOnScroll={false} // Disable zoom on scroll
                                                    regionStyle={{
                                                        initial: {
                                                        fill:  "#e4e4e4",
                                                        fillOpacity: 0.9, // Use camelCase for CSS properties
                                                        stroke: "none",
                                                        strokeWidth: 0,
                                                        strokeOpacity: 0,
                                                        height: "100%",
                                                        width: "100%",
                                                        },
                                                    }}
                                                    series={{
                                                        regions: [
                                                        {
                                                            attribute: "fill",
                                                            values: regionValues,
                                                            // scale: ["#C8EEFF", "#0071A4"],
                                                            scale: ["#C8EEFF", mapColor],
                                                                // normalizeFunction: "polynomial",
                                                            normalizeFunction: (value) => (value ? 1 : 0), // 1 if has value, 0 if not
                                                        },
                                                        ],
                                                    }}
                                                    onRegionTipShow={(e, el, code) => {
                                                        const values = regionValuesRef.current;
                                                        if (values[code]) {
                                                            el.html(`${el.html()}: ${values[code]}`);
                                                        } else {
                                                            el.html(`${el.html()}: 0`);
                                                        }
                                                    }}
                                                    zoomButtons={false} 
                                                />
                                        </div>
                                    </div>
                                </div>
                                <div className="row">

                                    <div className="col-12">
                                        <div className="flex justify-content-end" style={{ marginBottom: '5px'}}>

                                            <div className="form-inline navbar-search">
                                                <div style={{ position: 'relative', width: '100%' }}>
                                                    <input
                                                        type="text"
                                                        className="form-control bg-white border"
                                                        placeholder="Search"
                                                        aria-label="Search"
                                                        aria-describedby="basic-addon2"
                                                        value={searchValue}
                                                        onChange={(e) => setSearchValue(e.target.value)}
                                                        style={{
                                                            borderRadius: '32px'
                                                        }}
                                                    />
                                                    <span
                                                        className="searchIcon"
                                                        style={{
                                                            position: 'absolute',
                                                            right: 10,
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            color: '#888',
                                                            pointerEvents: 'none'
                                                        }}
                                                    >
                                                        <FaSearch />
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                    

                                <div className="row">
                                    <div
                                        className="col-12"
                                        style={{
                                            height: '260px',
                                            overflowY: 'auto',
                                        }}
                                    >
                                        {
                                            isComponentLoading ? (
                                                [...Array(25)].map((e, i) => (
                                                    <div className="row" key={i} style={{ marginBottom: '10px' }}>
                                                        <div className="col-12">
                                                            <div className="w-full flex justify-between items-center" style={{ padding: '2px 0' }}>
                                                                <div className="flex gap-2 align-items-center">
                                                                    <div style={{ fontSize: '35px', width: '40px' }}>
                                                                        <Skeleton variant="rounded" width={'100%'} height={35} style={{ borderRadius: '12px' }} />
                                                                    </div>
                                                                    <div>
                                                                        <div className="font-14" style={{ width: '150px', marginBottom: '5px' }}>
                                                                            <Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
                                                                        </div>
                                                                        <div className="text-gray-600" style={{ width: '100px' }}>
                                                                            <Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="flex gap-2 align-items-center">
                                                                    <div>
                                                                        <div className="text-gray-600" style={{ width: '100px' }}>
                                                                            <Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
                                                                        </div>
                                                                    </div>
                                                                    <div className="text-gray-600" style={{ width: '25px' }}>
                                                                        <Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                datas?.map((item, index) => (
                                                    <div
                                                        key={item.name || index}
                                                        className="row dashboard-country-list"
                                                        onClick={() => {
                                                            setShowModal(true);
                                                            setSelectedCountry(item.name);
                                                        }}
                                                    >
                                                        <div className="col-12">
                                                            <div className="w-full dashboard-country-rows justify-between items-center" style={{ padding: '2px 0' }}>
                                                                <div className="flex gap-2 align-items-center">
                                                                    <div style={{ fontSize: '30px' }}>
                                                                        <CountriesUIFlag countryName={item.name} />
                                                                    </div>
                                                                    <div>
                                                                        <div className="font-14">{item.name}</div>
                                                                        <div className="text-gray-600">{item.count} registered contacts</div>
                                                                    </div>
                                                                </div>
                                                                <div className="dashboard-country-progress-and-percentage-container">
                                                                    <div className="flex gap-2 align-items-center">
                                                                        <div className="dashboard-country-progress-bar-container">
                                                                            <div className="progress-bar-loader-container">
                                                                                <div className="progress-bar-loader"></div>
                                                                                <div 
                                                                                    className="progress-bar-loading"
                                                                                    style={{
                                                                                        width: `${item?.percentage}%`,
                                                                                    }}
                                                                                >
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div style={{ minWidth: '43px' }} className="text-right">{ `${item?.percentage}%` }</div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            )
                                        }
                                    </div>
                                </div>
                    
                                <div
                                    className="col-12"
                                    style={{
                                        marginTop: '10px',
                                        marginBottom: '-10px',
                                        textAlign: 'center',
                                        fontSize: '14px',
                                    }}
                                >
                                    Over <span className="text-primary fw-500">{mapList?.length}</span> countries registered
                                </div>
                            </>
                        )
                    }
                </div>
            </div>
            {
                showModal && (
                    <DemographicChartModal
                        countryName={selectedCountry}
                        onClose={() => {
                            setShowModal(false);
                            setSelectedCountry(null);
                        }}
                    />
                )
            }
            {
                showExportModal && (
                    <ExportPopupModal
                        isOpen={true}
                        onClose={handleClose}
                        widgetType={'CONTACT-LOCATIONS'}
                        widgetName={'Contact Locations'}
                    />
                )
            }
        </>
    )
}


export default DemographicChartComponent;